<template>
  <PDropdown :focus-on-open="false">
    <template #activator="{ open, close, isOpen }">
      <PInput
        ref="inputEl"
        :label="label"
        :help="help"
        :model-value="inputModel"
        @update:model-value="onInputValue($event, open, close)"
        :disabled="disabled"
        :placeholder="placeholder"
        @focus="filteredSuggestions.length && !disabled ? open() : noop()"
        @click="filteredSuggestions.length && !disabled ? open() : noop()"
        @keydown.shift.tab="onShiftTab(close)"
        @keydown.tab="onTab(isOpen)"
        @keydown.down.prevent="onInputKeydown(open, isOpen)"
        @keydown.up.prevent="onInputKeyup(open, isOpen)"
        @keydown.esc.stop="close"
        @keydown.enter="emit('update:modelValue', inputModel)"
        @blur="emit('update:modelValue', inputModel)"
        :append-icon="suggestions.length ? chevronDown : ''"
        :aria-expanded="isOpen"
        :hide-messages="true"
        aria-haspopup="listbox"
      />
    </template>

    <template #default="{ close }">
      <ul
        class="flex flex-col gap-1 items-start px-2 py-1 max-h-[400px]"
        @keydown.esc.stop="ondropdownEsc(close)"
        ref="list"
        role="listbox"
      >
        <li v-for="(suggestion, index) of filteredSuggestions" :key="index" class="w-full flex">
          <button
            @click="onSuggestion(suggestion, close)"
            ref="suggestionBtn"
            @keydown.down.prevent="onBtnKeyDown(index)"
            @keydown.up.prevent="onBtnKeyUp(index)"
            @keydown.tab="onBtnTab(index, close)"
            class="outline-offset-4 w-full text-left"
            :id="`suggestion-${index}`"
          >
            {{ suggestion.label }}
          </button>
          <PButton
            size="icon"
            mode="tertiary-borderless"
            aria-label="supprimer"
            :aria-describedby="`suggestion-${index}`"
            @click="removeSuggestion(suggestion.label, close)"
          >
            <OhVueIcon :name="deleteIcon" scale="0.8" />
          </PButton>
        </li>
      </ul>
    </template>
  </PDropdown>
</template>

<script setup lang="ts">
import { RiArrowDownSLine, RiDeleteBin6Line } from "oh-vue-icons/icons";
import { computed, ref, watch } from "vue";
import { useIcons, useMark, PInput, PDropdown, PButton } from "@pasta/box-v2";
import { noop } from "lodash";
import { OhVueIcon } from "oh-vue-icons";

const [chevronDown, deleteIcon] = useIcons(RiArrowDownSLine, RiDeleteBin6Line);

interface Props {
  label: string;
  suggestions: { label: string; value: string }[];
  modelValue: string;
  disabled?: boolean;
  help?: string;
  placeholder?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "remove", value: string): void;
}>();

const list = ref();
const inputEl = ref<any>(null);
const suggestionBtn = ref<HTMLButtonElement[] | null>(null);
const inputModel = ref();
const filteredSuggestions = computed(() => {
  return props.suggestions.filter(item => item.value.toLowerCase().includes(inputModel.value.toLowerCase()));
});

function removeSuggestion(label: string, close: () => void) {
  emit("remove", label);
  if (filteredSuggestions.value.length <= 1) {
    focusInput();
    close();
  }
}

const { updateMark } = useMark(
  () => list.value,
  () => inputModel.value
);
watch(() => inputModel.value, updateMark);

watch(
  () => props.modelValue,
  (val: string) => {
    inputModel.value = val;
  },
  { immediate: true }
);

function onInputKeyup(open: Function, isOpen: boolean) {
  if (!isOpen && props.suggestions.length) {
    open();
  }
  suggestionBtn.value?.at(-1)?.focus();
}
function onInputKeydown(open: Function, isOpen: boolean) {
  if (!isOpen && props.suggestions.length) {
    open();
  }
  suggestionBtn.value?.at(0)?.focus();
}

function onBtnKeyUp(index: number) {
  if (index === 0) {
    focusInput();
  } else {
    suggestionBtn.value?.at(index - 1)?.focus();
  }
}
function onBtnKeyDown(index: number) {
  if (index + 1 >= filteredSuggestions.value.length) {
    focusInput();
  } else {
    suggestionBtn.value?.at(index + 1)?.focus();
  }
}

function focusInput() {
  inputEl.value.mainElement.focus();
}

function ondropdownEsc(close: Function) {
  focusInput();
  close();
}

function onSuggestion(suggestion: { label: string; value: string }, close: Function) {
  inputModel.value = suggestion.value;
  emit("update:modelValue", inputModel.value);
  focusInput();
  close();
}

function onBtnTab(index: number, close: Function) {
  if (index + 1 >= filteredSuggestions.value.length) {
    close();
  }
}

function onInputValue(value: string, open: Function, close: Function) {
  inputModel.value = value;
  if (!filteredSuggestions.value.length) {
    close();
  } else {
    open();
  }
}

function onTab(isOpen: boolean) {
  if (!isOpen) {
    emit("update:modelValue", inputModel.value);
  }
}

function onShiftTab(close: Function) {
  emit("update:modelValue", inputModel.value);
  close();
}
</script>
